import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PHONE_TEL } from "./Nav";

const EASE = [0.16, 1, 0.3, 1];

const IMG = {
  emi: "https://customer-assets-lxgj4vgw.emergentagent.net/job_e0816016-db79-423a-a550-6522d024345b/artifacts/9ldkvs5z_emi%20photo.webp",
  blacklight:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_e0816016-db79-423a-a550-6522d024345b/artifacts/yenw4l02_blacklight.webp",
  mobile:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_e0816016-db79-423a-a550-6522d024345b/artifacts/ygfeuulc_truck%20and%20unit.webp",
  hauler:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_e0816016-db79-423a-a550-6522d024345b/artifacts/r4n3tnyv_18%20wheeler.webp",
  ut: "/images/ut.webp",
  utaWork: "/images/uta-work.webp",
  sea: "/images/sea.webp",
};

const SERVICE_LIST = [
  ["01", "Tubing Inspection", "In-house & mobile"],
  ["02", "Drill Pipe Inspection", "EMI, Reface, UT, SEA & more"],
  ["03", "Tool Inspection", "Bottom-hole assemblies & more"],
  ["04", "Blacklight / MPI", "UV magnetic particle"],
  ["05", "Cleaning & Coating", "Thread to full length"],
  ["06", "Water Blasting", "High-pressure washout"],
  ["07", "Storage", "Secure yard space"],
  ["08", "Trucking & Transport", "Flatbed pipe hauling"],
];

const GALLERY = [
  {
    src: IMG.emi,
    span: "md:col-span-7",
    caption: "FIELD CREW — EMI INSPECTION AT THE RACK",
    tag: "Mobile Unit 01",
    testid: "gallery-card-emi",
  },
  {
    src: IMG.blacklight,
    span: "md:col-span-5",
    caption: "BLACKLIGHT — MAGNETIC PARTICLE INDICATION",
    tag: "UV / MPI",
    testid: "gallery-card-blacklight",
  },
  {
    src: IMG.utaWork,
    span: "md:col-span-5",
    caption: "SCANNING UNIT",
    tag: "UTA",
    testid: "gallery-card-scanner",
  },
  {
    src: IMG.mobile,
    span: "md:col-span-7",
    caption: "MOBILE INSPECTION UNITS — WE COME TO YOU",
    tag: "On Location",
    testid: "gallery-card-mobile",
  },
  {
    src: IMG.ut,
    span: "md:col-span-7",
    caption: "ULTRASONIC TESTING",
    tag: "UT",
    testid: "gallery-card-marking",
  },
  {
    src: IMG.sea,
    span: "md:col-span-5",
    caption: "END AREA PREP — MAG PARTICLE INSPECTION",
    tag: "SEA",
    testid: "gallery-card-prep",
  },
  {
    src: IMG.hauler,
    span: "md:col-span-12",
    aspect: "aspect-[4/3] md:aspect-[21/9]",
    caption: "FLEET — FLATBED PIPE HAULING & TRANSPORT",
    tag: "USDOT 3801841",
    testid: "gallery-card-hauler",
  },
];

function GalleryCard({ item, index }) {
  return (
    <motion.a
      href={PHONE_TEL}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.06 * index }}
      className={`group relative block overflow-hidden frame-chamfer bg-steel ${item.span}`}
      data-testid={item.testid}
    >
      <div className={`relative w-full overflow-hidden ${item.aspect ?? "aspect-[4/3] md:aspect-[16/10]"}`}>
        <img
          src={item.src}
          alt={item.caption}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" aria-hidden="true" />
        <div className="absolute inset-0 bg-uv/0 transition-colors duration-500 group-hover:bg-uv/[0.06]" aria-hidden="true" />
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 sm:p-6">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-uv">{item.tag}</span>
          <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-slate-100 sm:text-sm">{item.caption}</p>
        </div>
        <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-300 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-uv" />
      </div>
    </motion.a>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative border-t border-white/10 bg-steel py-24 sm:py-32" data-testid="services-section">
      <div className="mx-auto w-full max-w-[1700px] px-4 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-uv">Services & Fleet</span>
          <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-slate-500">08 Lines of Work</span>
        </motion.div>

        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Index list */}
          <div className="lg:col-span-4">
            <motion.h2
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="font-display text-4xl font-semibold uppercase tracking-tight text-slate-50 sm:text-5xl"
            >
              What we run.
            </motion.h2>
            <ul className="mt-10" data-testid="services-index-list">
              {SERVICE_LIST.map(([n, name, note], i) => (
                <motion.li
                  key={n}
                  initial={{ opacity: 0, x: -18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.04 * i }}
                  className="group flex items-baseline gap-4 border-b border-white/10 py-4 transition-colors duration-300 hover:border-uv/40"
                >
                  <span className="font-mono text-xs text-uv">{n}</span>
                  <span className="font-display text-lg font-medium uppercase tracking-tight text-slate-100 transition-transform duration-300 group-hover:translate-x-1.5 sm:text-xl">
                    {name}
                  </span>
                  <span className="ml-auto hidden font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 sm:block">
                    {note}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Bento gallery */}
          <div className="grid gap-5 md:grid-cols-12 lg:col-span-8">
            {GALLERY.map((item, i) => (
              <GalleryCard key={item.testid} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
